import React, { useState, useEffect } from 'react';
import { useParams, useNavigate , Navigate} from 'react-router-dom';
import './UserDetails.css'; // Create a corresponding CSS file

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.startsWith(name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

function UserDetails() {
  const { id } = useParams(); // Get user ID from URL
  const [user, setUser] = useState(null);
  const [currentUser, setCurrentUser] = useState(null); // Logged-in user
  const [isEditing, setIsEditing] = useState(false);
  const navigate = useNavigate();
  const [currentUserLoaded, setCurrentUserLoaded] = useState(false); // Track loading state

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    avg_distance: '',
    avg_duration: '',
    elevation_diff_up: '',
    elevation_diff_down: '',
    favorite_books: ['', '', ''],
    favorite_movies: ['', '', ''],
    favorite_bands: ['', '', ''],
  });
  const [profilePicFile, setProfilePicFile] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');

  // Fetch the user being viewed
useEffect(() => {
  // Fetch the user being viewed
  fetch(`/api/users/${id}/`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  })
    .then((res) => {
      if (!res.ok) throw new Error('Failed to fetch user details');
      return res.json();
    })
    .then((data) => {
      setUser(data);
      setFormData({
        username: data.username,
        email: data.email,
        avg_distance: data.avg_distance || '',
        avg_duration: data.avg_duration || '',
        elevation_diff_up: data.elevation_diff_up || '',
        elevation_diff_down: data.elevation_diff_down || '',
        favorite_books: Array.isArray(data.favorite_books) && data.favorite_books.length > 0
          ? data.favorite_books
          : ['', '', ''],
        favorite_movies: Array.isArray(data.favorite_movies) && data.favorite_movies.length > 0
          ? data.favorite_movies
          : ['', '', ''],
        favorite_bands: Array.isArray(data.favorite_bands) && data.favorite_bands.length > 0
          ? data.favorite_bands
          : ['', '', ''],
      });
    })
    .catch((err) => console.error(err));

  // Fetch the current logged-in user
  fetch('/api/user/', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  })
    .then((res) => {
      if (res.ok) return res.json();
      return null;
    })
    .then((data) => setCurrentUser(data))
    .catch((err) => console.error('Error fetching current user:', err));
}, [id]);

  const handleSetupPayments = async () => {
    try {
      const res = await fetch('/api/create-express-account/', {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (data.url) {
        // Redirect to Stripe's hosted onboarding page
        window.location.href = data.url;
      } else {
        alert('Error setting up payments: ' + data.error);
      }
    } catch (err) {
      console.error(err);
      alert('An error occurred.');
    }
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleArrayChange = (e, field, index) => {
    const newArray = [...formData[field]];
    newArray[index] = e.target.value;
    setFormData({ ...formData, [field]: newArray });
  };

  const handleFileSelect = (e) => {
    setProfilePicFile(e.target.files[0]);
  };

  const handleUploadProfilePic = () => {
    if (!profilePicFile) return;
    const formDataUpload = new FormData();
    formDataUpload.append('profile_picture', profilePicFile);

    fetch('/api/user/upload-profile-picture/', {
      method: 'POST',
      credentials: 'include',
      headers: {
	      'X-CSRFToken': getCookie('csrftoken'),   // <-- Add this
	    },

	      body: formDataUpload,
	    })
      .then((res) => {
        if (!res.ok) throw new Error('Upload failed');
        return res.json();
      })
      .then((data) => {
        if (data.profile_picture_url) {
          setUser((prev) => ({ ...prev, profile_picture: data.profile_picture_url }));
          alert('Profile picture updated!');
        } else {
          alert('Error updating profile picture.');
        }
      })
      .catch((err) => {
        console.error('Error uploading profile pic:', err);
        alert('Error uploading profile picture.');
      });
  };

const handleSave = () => {
  const { username, ...dataToUpdate } = formData; // Exclude 'username'

  // Process numerical fields: convert to numbers or null
  const processedData = {
    email: dataToUpdate.email,
    avg_distance: dataToUpdate.avg_distance === '' ? null : parseFloat(dataToUpdate.avg_distance),
    avg_duration: dataToUpdate.avg_duration === '' ? null : parseFloat(dataToUpdate.avg_duration),
    elevation_diff_up: dataToUpdate.elevation_diff_up === '' ? null : parseFloat(dataToUpdate.elevation_diff_up),
    elevation_diff_down: dataToUpdate.elevation_diff_down === '' ? null : parseFloat(dataToUpdate.elevation_diff_down),
    favorite_books: dataToUpdate.favorite_books.filter(book => book.trim() !== ''),
    favorite_movies: dataToUpdate.favorite_movies.filter(movie => movie.trim() !== ''),
    favorite_bands: dataToUpdate.favorite_bands.filter(band => band.trim() !== ''),
  };

  fetch(`/api/user/update/`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': getCookie('csrftoken'),
    },
    credentials: 'include',
    body: JSON.stringify(processedData),
  })
    .then(async (res) => {
      if (!res.ok) {
        const errorData = await res.json();
        throw errorData;
      }
      return res.json();
    })
    .then((data) => {
      setUser(data.user);
      setStatusMsg(data.message);
      setIsEditing(false);
    })
    .catch((err) => {
      console.error('Update Error:', err);
      if (err.errors) {
        const formattedErrors = Object.entries(err.errors).map(
          ([field, messages]) => `${field}: ${messages.join(', ')}`
        ).join('\n');
        setStatusMsg(`Error updating profile:\n${formattedErrors}`);
      } else {
        setStatusMsg('An unexpected error occurred.');
      }
    });
};

  if (!user || !currentUser) {
    return <div>Loading user details...</div>;
  }

  const isOwnProfile = currentUser.id === user.id;

  return (
    <div className="user-details-container">
    <button
        onClick={() => navigate('/')}
        className="home-button"
      >
        Home
      </button>

      <h2>User Details</h2>
      <div className="profile-pic-section">
        <img
          src={user.profile_picture || '/default-avatar.png'}
          alt={`${user.username}'s profile`}
          className="profile-picture"
        />
        {isOwnProfile && (
          <div className="upload-section">
            <input type="file" onChange={handleFileSelect} />
            <button onClick={handleUploadProfilePic}>Upload Picture</button>
          </div>
        )}
      </div>

      {isEditing ? (
        <div className="edit-form">
          <label>Username:</label>
          <input
            type="text"
            name="username"
            value={formData.username}
            onChange={handleInputChange}
            disabled={!isOwnProfile}
          />


          {/* Hiking Statistics */}
          <div className="additional-info">
            <h3>Hiking Statistics</h3>

            <label>Average Hike Distance (km):</label>
            <input
              type="number"
              name="avg_distance"
              value={formData.avg_distance}
              onChange={handleInputChange}
              disabled={!isOwnProfile}
            />

            <label>Average Hike Duration (hrs):</label>
            <input
              type="number"
              name="avg_duration"
              value={formData.avg_duration}
              onChange={handleInputChange}
              disabled={!isOwnProfile}
            />

            <label>Elevation Difference Up (m):</label>
            <input
              type="number"
              name="elevation_diff_up"
              value={formData.elevation_diff_up}
              onChange={handleInputChange}
              disabled={!isOwnProfile}
            />

            <label>Elevation Difference Down (m):</label>
            <input
              type="number"
              name="elevation_diff_down"
              value={formData.elevation_diff_down}
              onChange={handleInputChange}
              disabled={!isOwnProfile}
            />
          </div>

          {/* Favorites */}
          <div className="additional-info">
            <h3>Favorites</h3>

            {/* Favorite Books */}
            <div className="info-group">
              <label>Favorite Books:</label>
              {formData.favorite_books.map((book, index) => (
                <input
                  key={index}
                  type="text"
                  value={book}
                  onChange={(e) => handleArrayChange(e, 'favorite_books', index)}
                  placeholder={`Book ${index + 1}`}
                  disabled={!isOwnProfile}
                />
              ))}
            </div>

            {/* Favorite Movies/Series */}
            <div className="info-group">
              <label>Favorite Movies/Series:</label>
              {formData.favorite_movies.map((movie, index) => (
                <input
                  key={index}
                  type="text"
                  value={movie}
                  onChange={(e) => handleArrayChange(e, 'favorite_movies', index)}
                  placeholder={`Movie/Series ${index + 1}`}
                  disabled={!isOwnProfile}
                />
              ))}
            </div>

            {/* Favorite Music Bands */}
            <div className="info-group">
              <label>Favorite Music Bands:</label>
              {formData.favorite_bands.map((band, index) => (
                <input
                  key={index}
                  type="text"
                  value={band}
                  onChange={(e) => handleArrayChange(e, 'favorite_bands', index)}
                  placeholder={`Band ${index + 1}`}
                  disabled={!isOwnProfile}
                />
              ))}
            </div>
          </div>

          {isOwnProfile && (
            <div className="form-actions">
              <button onClick={handleSave}>Save</button>
              <button onClick={() => setIsEditing(false)}>Cancel</button>
            </div>
          )}
        </div>
      ) : (
        <div className="user-info">
          <p><strong>Username:</strong> {user.username}</p>

          {/* Hiking Statistics */}
          <div className="additional-info">
            <h3>Hiking Statistics</h3>
            <p><strong>Average Hike Distance:</strong> {user.avg_distance} km</p>
            <p><strong>Average Hike Duration:</strong> {user.avg_duration} hrs</p>
            <p><strong>Elevation Difference Up:</strong> {user.elevation_diff_up} m</p>
            <p><strong>Elevation Difference Down:</strong> {user.elevation_diff_down} m</p>
          </div>

          {/* Favorites */}
          <div className="additional-info">
            <h3>Favorites</h3>
            <p><strong>Books:</strong> {user.favorite_books.join(', ') || 'N/A'}</p>
            <p><strong>Movies/Series:</strong> {user.favorite_movies.join(', ') || 'N/A'}</p>
            <p><strong>Music Bands:</strong> {user.favorite_bands.join(', ') || 'N/A'}</p>
          </div>

	{!isEditing && (
	  <div className="button-container">
	    {isOwnProfile && (
	      <>
		<button onClick={() => setIsEditing(true)} className="edit-button">
		  Edit Profile
		</button>
		{currentUser && currentUser.id === user.id && !user.stripe_account_id && (
		  <button onClick={handleSetupPayments} className="edit-button">
		    Setup Payments
		  </button>
		)}
	      </>
	    )}
	  </div>
	)}

      {/* Optionally, if already set up, you might show a message or a link to view account details */}
      {currentUser && currentUser.id === user.id && user.stripe_account_id && (
        <p>Payments are enabled for your account.</p>
      )}



          {statusMsg && <p className="status-msg">{statusMsg}</p>}
        </div>
      )}
    </div>
  );
}

export default UserDetails;

